import { BrowserFourdApi } from '.';
import { BaseRequest } from '../../api';
import { system } from '../../api/system';

export class System implements system.Api {
  private _systemInfo: system.SystemInfo;
  private _systemFeaturesList: system.FeatureList;

  constructor(private fourd: BrowserFourdApi) {}

  openTwitter(request: BaseRequest): void {
    this.fourd.call4Dv2('system.openTwitter', request);
  }

  close(): void {
    this.fourd.call4Dv2('system.close');
  }

  hide(): void {
    this.fourd.call4Dv2('system.hide');
  }

  wait(request?: system.MessageRequest): void {
    this.fourd.call4Dv2('system.wait', request);
  }

  resume(): void {
    this.fourd.call4Dv2('system.resume');
  }

  cancelClose(): void {
    this.fourd.call4Dv2('system.cancelClose');
  }

  alert(request: system.MessageRequest): void {
    if (!this.isString(request.message) || !request.message) {
      return;
    }
    this.fourd.call4Dv2('system.alert', request);
  }

  error(request: system.MessageRequest): void {
    if (!this.isString(request.message) || !request.message) {
      return;
    }
    this.fourd.call4Dv2('system.error', request);
  }

  openDialog(request: system.DialogRequest): Promise<boolean> {
    if (!this.isString(request.message) || !request.message) {
      throw new Error('Invalid value in message');
    } else if (!request.dialogType) {
      throw new Error('DialogType cannot be null');
    }

    return this.fourd.twoWayCall<system.DialogRequest, boolean>(
      request,
      (requestId, request) => {
        this.fourd.call4Dv2('system.openDialog', request, requestId);
      },
      (response) => {
        return response;
      }
    );
  }

  openDialogV2(request: system.DialogRequest): Promise<system.DialogButtonType> {
    if (!this.isString(request.message) || !request.message) {
      throw new Error('Invalid value in message');
    } else if (!request.dialogType) {
      throw new Error('DialogType cannot be null');
    }

    return this.fourd.twoWayCall<system.DialogRequest, system.DialogButtonType>(
      request,
      (requestId, request) => {
        this.fourd.call4Dv2('system.openDialog', request, requestId);
      },
      (response) => {
        if (response === true) return system.DialogButtonType.Confirm;
        else if (response === false) return system.DialogButtonType.Refuse;

        return response;
      }
    );
  }

  openUrl(config: system.BrowserConfig): void {
    config['action'] = 'OpenURL';
    this.fourd.call4Dv2('system.openUrl', config);
  }

  openUrlV2(config: system.BrowserConfigV2): void {
    const { showControls, ...browserConfig } = config as system.BaseBrowserConfig & system.FourdBrowserConfigV2;
    const v1Config: system.BrowserConfig = browserConfig;
    if (showControls !== undefined) {
      (v1Config as system.FourdBrowserConfig).hideControls = showControls;
    }
    v1Config['action'] = 'OpenURL';
    this.fourd.call4Dv2('system.openUrl', v1Config);
  }

  getSystemInfo(): Promise<system.SystemInfo> {
    if (this._systemInfo) {
      return Promise.resolve(this._systemInfo);
    }
    return this.fourd.twoWayCall<system.SystemInfo, system.SystemInfo>(
      null,
      (requestId, request) => {
        this.fourd.call4Dv2('system.getSystemInfo', request, requestId);
      },
      (response) => {
        this._systemInfo = response;
        return response;
      }
    );
  }

  getFeatureList(forceRefresh?: boolean): Promise<system.FeatureList> {
    if (this._systemFeaturesList && !forceRefresh) {
      return Promise.resolve(this._systemFeaturesList);
    }
    return this.fourd.twoWayCall<undefined, system.FeatureList>(
      null,
      (requestId, request) => {
        this.fourd.call4Dv2('system.getFeatureList', request, requestId);
      },
      (response) => {
        try {
          const parsedResponse = JSON.parse(response) as system.FeatureList;
          this._systemFeaturesList = parsedResponse;
          return parsedResponse;
        } catch (e) {
          this._systemFeaturesList = response;
          return response;
        }
      }
    );
  }

  setWindowTitle(request: system.SetWindowTitleRequest) {
    this.fourd.call4Dv2('system.setWindowTitle', request);
  }

  onBeforeClose(handler: () => Promise<boolean>): void {
    handler().then((shouldCancelClose) => {
      if (shouldCancelClose) {
        this.cancelClose();
      }
    });
  }

  async send(request: any, hasResponse: boolean = false): Promise<any> {
    if (!this.fourd.isTrustedApp) {
      throw new Error('Permission error');
    }

    if (request === null || request === undefined) {
      throw new Error('Bad request');
    }

    if (hasResponse) {
      return this.fourd.internalSendAsync(request);
    }

    this.fourd.internalSend(request);
    return Promise.resolve();
  }

  finishLoading(): void {
    this.fourd.call4Dv2('system.finishLoading');
  }

  enableCloseConfirmation(request: system.enableCloseConfirmationRequest): void {
    // this method is only for tackling the LEAP web window problem at the moment
  }

  private isString = (value: any) => {
    return typeof value === 'string' || value instanceof String;
  };
}
